// The viewer builds its scene from three/webgpu, so the override material and render
// target have to come from the same entry point - the WebGL build's classes are a
// different set and its plain materials do not compile under the node pipeline.
// eslint-disable-next-line import/no-unresolved -- exports subpath; the node resolver cannot follow it
import { Box3, Color, DoubleSide, Fog, MeshBasicNodeMaterial, RenderTarget, Vector3 } from 'three/webgpu'

import { getModelObject, getViewerInternals } from './viewerInternals'

// Square, and the usual ControlNet depth input size. Fixed rather than derived from the
// canvas so the same camera pose always yields the same map, whatever the window size.
const DEPTH_SIZE = 1024
// Corner-to-corner fractions used to subdivide the bounding box. Fitting the fog to the
// 8 corners alone clips geometry that bulges out of a face toward the camera, so each
// edge contributes two interior points as well.
const SUBDIVISION_RATIOS = [1 / 3, 2 / 3]
// Headroom kept either side of the fitted range in the contrast pass, in 0-255 terms, so
// the nearest and farthest surfaces do not clamp flat to pure white/black.
const CONTRAST_MARGIN = 2

const BLACK = new Color(0x000000)

// Every point the fog range has to cover: the box corners plus points along each
// corner pair, so a face bulging toward the camera still lands inside the range.
const collectSamplePoints = (model) => {
  const box = new Box3().setFromObject(model)
  const corners = [
    new Vector3(box.min.x, box.min.y, box.min.z),
    new Vector3(box.min.x, box.min.y, box.max.z),
    new Vector3(box.min.x, box.max.y, box.min.z),
    new Vector3(box.min.x, box.max.y, box.max.z),
    new Vector3(box.max.x, box.min.y, box.min.z),
    new Vector3(box.max.x, box.min.y, box.max.z),
    new Vector3(box.max.x, box.max.y, box.min.z),
    new Vector3(box.max.x, box.max.y, box.max.z),
  ]

  const points = [...corners]
  corners.forEach((corner, index) => {
    corners.forEach((other, otherIndex) => {
      if (index === otherIndex) {
        return
      }
      SUBDIVISION_RATIOS.forEach((ratio) => {
        points.push(new Vector3().lerpVectors(other, corner, ratio))
      })
    })
  })

  return { points, box }
}

// Near/far measured from where the camera actually is, which is what makes the map
// reflect the pose the user left the model in.
const fitFogRange = (model, camera) => {
  const { points, box } = collectSamplePoints(model)

  let near = Infinity
  let far = 0
  points.forEach((point) => {
    const distance = camera.position.distanceTo(point)
    near = Math.min(near, distance)
    far = Math.max(far, distance)
  })

  // Standing inside the model's own bounds, everything in front of the camera is within
  // arm's reach; keeping the fitted near would render the whole frame white.
  if (box.containsPoint(camera.position)) {
    near = 0.05
  }

  return { near, far }
}

const renderToBuffer = async (renderer, scene, camera, target, size) => {
  renderer.setRenderTarget(target)
  await renderer.renderAsync(scene, camera)
  renderer.setRenderTarget(null)

  return renderer.readRenderTargetPixelsAsync(target, 0, 0, size, size)
}

// The first pass rarely spans the full 0-255 range, which costs the map contrast where
// it matters. Re-fit the fog to the range the render actually produced and draw again.
const applyContrast = async (renderer, scene, camera, target, size, buffer) => {
  let min = 255
  let max = 0
  for (let i = 0; i < buffer.length; i += 4) {
    min = Math.min(min, buffer[i])
    max = Math.max(max, buffer[i])
  }

  const fog = scene.fog
  if (max <= min || !(fog instanceof Fog)) {
    return buffer
  }

  const { near, far } = fog
  const span = far - near
  fog.near = near + (span * (255 - Math.min(max + CONTRAST_MARGIN, 255))) / 255
  fog.far = near + (span * (255 - Math.max(min - CONTRAST_MARGIN, 0))) / 255

  return renderToBuffer(renderer, scene, camera, target, size)
}

const toPngDataUrl = (buffer, size) => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const context = canvas.getContext('2d')
  context.putImageData(new ImageData(new Uint8ClampedArray(buffer), size, size), 0, 0)

  return canvas.toDataURL('image/png')
}

async function renderDepthPass(viewer, { size = DEPTH_SIZE } = {}) {
  const internals = getViewerInternals(viewer)
  if (!internals) {
    return null
  }

  const { scene, camera, renderer } = internals
  const model = getModelObject(viewer, scene)
  if (!model) {
    return null
  }

  const previous = {
    overrideMaterial: scene.overrideMaterial,
    background: scene.background,
    fog: scene.fog,
    aspect: camera.aspect,
  }
  const target = new RenderTarget(size, size)
  const material = new MeshBasicNodeMaterial({ side: DoubleSide, toneMapped: false })

  try {
    const { near, far } = fitFogRange(model, camera)

    scene.overrideMaterial = material
    scene.background = BLACK
    scene.fog = new Fog(0x000000, near, far)
    // The map is square while the viewport rarely is, so widen the frustum to match
    // rather than letting the render stretch.
    camera.aspect = 1
    camera.updateProjectionMatrix()

    const firstPass = await renderToBuffer(renderer, scene, camera, target, size)
    const buffer = await applyContrast(renderer, scene, camera, target, size, firstPass)

    return toPngDataUrl(buffer, size)
  } finally {
    scene.overrideMaterial = previous.overrideMaterial
    scene.background = previous.background
    scene.fog = previous.fog
    camera.aspect = previous.aspect
    camera.updateProjectionMatrix()
    renderer.setRenderTarget(null)
    target.dispose()
    material.dispose()
  }
}

// The pass borrows the viewer's one shared scene, swapping its material, fog and
// background and putting them back afterwards. Two passes at once interleave those
// swaps - the second captures the first's temporary state as the thing to restore - and
// the viewer is left stuck in depth mode. A double-clicked Render button is enough to
// trigger it, so a second call rides the first instead of starting its own.
let inFlight = null

/**
 * Renders the loaded model as a depth map from the viewer's current camera pose.
 *
 * Depth comes from black linear fog over a flat unlit override material rather than from
 * the depth buffer: near surfaces stay bright, far ones fade to the black background,
 * which is the shape ControlNet-style depth conditioning expects. Ported from CGDream's
 * Viewer3D/utils/createImage - minus its Y-flip, which corrects a WebGL readback whose
 * origin is bottom-left; the WebGPU readback here is already top-down.
 *
 * @param {object} viewer - The live @cgtrader/cgt-viewer instance.
 * @param {{ size?: number }} [options]
 * @returns {Promise<string|null>} A PNG data URL, or null when the viewer cannot provide a scene.
 */
export default function renderDepthMap(viewer, options) {
  if (inFlight) {
    return inFlight
  }

  inFlight = renderDepthPass(viewer, options).finally(() => {
    inFlight = null
  })

  return inFlight
}
